import { PencilSimpleIcon } from "@/components/utils/icons/PencilSimpleIcon";
import { Colors } from "@/enums/Colors";
import { LazoChatConversationShell } from "@/models/ai-chat/LazoChatConversation";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import { Box, outlinedInputClasses, styled, TextField, Typography } from "@mui/material";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type ChatBarTitleProps = {
  conversation: LazoChatConversationShell;
  onChatSelect: (chat: LazoChatConversationShell) => void;
  setChats: Dispatch<SetStateAction<LazoChatConversationShell[]>>;
};

function ChatBarTitle(props: ChatBarTitleProps) {
  const { t } = useTranslation("chat");
  const [title, setTitle] = useState<string>("");
  const [editingTitle, setEditingTitle] = useState<boolean>(false);
  const editableTitle = !!props.conversation && (props.conversation.isOwner === undefined || props.conversation.isOwner);

  useEffect(() => {
    if (!props.conversation) {
      return;
    }
    setTitle(props.conversation.title);
    setEditingTitle(false);
  }, [props.conversation]);

  function handleRename() {
    if (title === "" || title === props.conversation.title) {
      setTitle(props.conversation.title);
      setEditingTitle(false);
      return;
    }
    LazoChatConversationService.renameConversation(props.conversation.externalId, title).then((r) => {
      r.onSuccess((c) => {
        props.setChats((prev) =>
          prev.map((p) => {
            if (p.externalId === c.externalId) {
              return { ...p, title: c.title };
            }
            return p;
          })
        );
        props.onChatSelect({ ...props.conversation, title: c.title });
      });
    });
  }

  function handleRenameKeyPress(ev: React.KeyboardEvent) {
    if (ev.key === "Enter") {
      ev.preventDefault();
      handleRename();
    }
  }

  return (
    <>
      {!editingTitle ? (
        <Box display="flex" gap="0.25rem" alignItems="center" justifyContent="center">
          <ChatBarTitleTypography
            onMouseEnter={() => editableTitle}
            onMouseLeave={() => editableTitle}
            onClick={() => editableTitle && setEditingTitle(true)}
            editable={editableTitle}
          >
            {props.conversation ? props.conversation.title : t("chatBar.newMessage")}
            {editableTitle && <PencilSimpleIcon sx={{ fontSize: "1rem" }} />}
          </ChatBarTitleTypography>
        </Box>
      ) : (
        <StyledTextField
          onBlur={handleRename}
          onKeyDown={handleRenameKeyPress}
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      )}
    </>
  );
}

const ChatBarTitleTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "editable",
})<{
  editable: boolean;
}>(({ editable }) => ({
  display: "flex",
  gap: "0.5rem",
  alignItems: "center",
  margin: "0.5rem",
  fontWeight: "600",
  marginTop: ".5rem",
  fontSize: "0.875rem",
  padding: "0.25rem",
  ...(editable && {
    ":hover": {
      backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
      borderRadius: "0.25rem",
    },
  }),
}));

const StyledTextField = styled(TextField)(() => ({
  [`& .${outlinedInputClasses.root}`]: {
    fontSize: "0.75rem",
    fontWeight: 600,
    "&.Mui-focused": {
      outline: 0,
      border: 0,
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        border: `1px solid ${Colors.BUTTON_PURPLE}`,
      },
    },
    [`& .${outlinedInputClasses.input}`]: {
      padding: "0.75rem",
    },
    [`& .${outlinedInputClasses.notchedOutline}`]: {
      borderRadius: "0.5rem",
      border: `1px solid ${Colors.BUTTON_PURPLE}`,
    },
  },
}));

export default ChatBarTitle;
